import { convexQuery } from "@convex-dev/react-query";
import { api } from "@selca/backend/api";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useState } from "react";
import { useAuth } from "../hooks/use-auth";
import { useClientStore } from "../store";
import { ImagesDetailPanel } from "./images-page/ImagesDetailPanel";
import { ImagesGrid } from "./images-page/ImagesGrid";
import { SizeSlider } from "./images-page/SizeSlider";
import { LoadingState } from "./LoadingState";
import { LoggedOutView } from "./LoggedOutView";

type RequestStage =
  | "collecting"
  | "ready"
  | "processing"
  | "completed"
  | "failed"
  | "expired"
  | "rejected";

export type MappedRequest = {
  _id: string;
  stage: RequestStage;
  createdAt: number;
  promptText?: string;
  previewUrl?: string;
};

const PANEL_WIDTH_PERCENT = 35;

function EmptyState() {
  return (
    <motion.div
      animate={{ opacity: 1 }}
      className="flex w-full items-center justify-center"
      initial={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className="flex max-w-md flex-col items-center gap-6 text-center"
        initial={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.5 }}
      >
        <motion.video
          animate={{ opacity: 1, scale: 1 }}
          autoPlay
          className="size-20"
          initial={{ opacity: 0, scale: 0.9 }}
          loop
          muted
          onCanPlay={(e) => {
            e.currentTarget.playbackRate = 2;
          }}
          playsInline
          src="/bear_alpha_smooth.webm"
          transition={{ duration: 0.5, ease: "easeOut" }}
        />
        <div>
          <h3 className="font-display text-4xl font-bold tracking-tight text-foreground">
            No images yet
          </h3>
          <p className="mt-4 text-base text-muted-foreground">
            Send an image to start creating
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}

export function ImagesPage() {
  const { isAuthenticated, isPending } = useAuth();
  const { imageSize, setImageSize, isMobile } = useClientStore();
  const [selectedRequest, setSelectedRequest] = useState<MappedRequest | null>(
    null
  );

  const { data: requests, isLoading } = useQuery({
    ...convexQuery(api.requests.getMyRequests, {}),
    enabled: isAuthenticated && !isPending,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

  if (isPending) {
    return null;
  }

  if (!isAuthenticated) {
    return <LoggedOutView />;
  }

  const isInitialLoading = isLoading || requests === undefined;

  const mappedRequests: MappedRequest[] = (requests ?? []).map((r) => ({
    _id: r._id,
    stage: r.stage as RequestStage,
    createdAt: r.createdAt,
    promptText: r.promptText ?? undefined,
    previewUrl:
      r.result?.imageUrls?.[0] ??
      r.inputMedia?.[0]?.storedUrl ??
      r.inputMedia?.[0]?.providerUrl,
  }));

  const hasRequests = mappedRequests.length > 0;
  const isPanelOpen = selectedRequest !== null;

  return (
    <div className="flex w-full h-full flex-col min-w-0 min-h-0 overflow-hidden">
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className="flex shrink-0 items-center justify-between border-zinc-200 py-6 dark:border-zinc-800 mt-12 md:mt-0"
        initial={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        <div>
          {!isMobile && (
            <h1 className="font-display text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              Your Creations
            </h1>
          )}
          <p className="mt-4 md:mt-2 text-left text-sm text-muted-foreground">
            {isInitialLoading
              ? "Loading your creations..."
              : hasRequests
                ? `${mappedRequests.length} request${mappedRequests.length === 1 ? "" : "s"}`
                : "No requests yet"}
          </p>
        </div>
      </motion.div>

      <div className="flex flex-1 min-h-0 gap-4 pb-6 pr-2">
        <div
          className="flex h-full flex-col min-w-0"
          style={{
            width:
              isPanelOpen && !isMobile
                ? `${100 - PANEL_WIDTH_PERCENT}%`
                : "100%",
          }}
        >
          <div className="flex-1 min-h-0 overflow-auto scrollbar-hide pb-20 w-full px-1 pt-1">
            {isInitialLoading ? (
              <div className="flex flex-1 items-center justify-center">
                <LoadingState />
              </div>
            ) : hasRequests ? (
              <ImagesGrid
                imageSize={imageSize}
                onSelect={setSelectedRequest}
                requests={Array(10)
                  .fill(null)
                  .flatMap(() => mappedRequests)}
                selectedId={selectedRequest?._id}
              />
            ) : (
              <div className="flex flex-1 items-center justify-center pb-24">
                <EmptyState />
              </div>
            )}
          </div>

          {hasRequests && (
            <SizeSlider
              max={120}
              min={30}
              onChange={setImageSize}
              value={imageSize}
            />
          )}
        </div>

        <ImagesDetailPanel
          isMobile={isMobile}
          onClose={() => setSelectedRequest(null)}
          open={isPanelOpen}
          request={selectedRequest}
          widthPercent={PANEL_WIDTH_PERCENT}
        />
      </div>
    </div>
  );
}
